import type { BrowserStreamReport } from '../../../../src/shared/agenticStreamTelemetry';

const abortSources = new WeakMap<AbortSignal, string>();
export function setStreamAbortSource(signal: AbortSignal, source: string): void {
  if (!signal.aborted) abortSources.set(signal, source);
}

export function createStreamTelemetry(options: {
  signal?: AbortSignal | null; clientRequestId?: string;
  onTelemetry?: (event: BrowserStreamReport) => void;
}) {
  const started = Date.now();
  let clientRequestId = options.clientRequestId ?? null;
  let firstByte: number | null = null, lastByte: number | null = null, lastFrame: number | null = null;
  let bytes = 0, frames = 0, reconnectAttempts = 0, lastReceivedEventId: string | null = null;
  let invocationId: string | null = null, httpRequestId: string | null = null;
  let initialHttpRequestId: string | null = null;
  let connectionIndex = 0, emittedConnections = 0;
  let finished = false;
  const attempts: Array<{ status: number | null; errorName: string | null }> = [];
  const errorName = (error: unknown) => error && typeof (error as Error).name === 'string' ? (error as Error).name.slice(0, 80) : null;
  const emit = (event: BrowserStreamReport['event'], outcome: BrowserStreamReport['outcome'], error?: unknown) => {
    const now = Date.now();
    const report: BrowserStreamReport = {
      event, outcome, observation: 'browser_receipt', timestamp: new Date(now).toISOString(), elapsedMs: now - started,
      clientRequestId, invocationId, httpRequestId, initialHttpRequestId,
      connectionIndex, firstByteReceivedAt: firstByte == null ? null : new Date(firstByte).toISOString(),
      lastByteReceivedAt: lastByte == null ? null : new Date(lastByte).toISOString(),
      lastFrameReceivedAt: lastFrame == null ? null : new Date(lastFrame).toISOString(),
      msSinceLastByte: lastByte == null ? null : now - lastByte, bytesReceived: bytes, framesReceived: frames,
      lastReceivedEventId, reconnectAttempts, attempts: [...attempts],
      signalAborted: options.signal?.aborted ?? false, abortSource: options.signal ? abortSources.get(options.signal) ?? 'unknown' : 'unknown',
      abortReason: options.signal?.aborted ? errorName(options.signal.reason) ?? 'unavailable' : null,
      errorName: errorName(error),
      // Native network errors contain no payload. Bound and redact URLs defensively.
      errorMessage: error instanceof Error ? error.message.replace(/https?:\/\/\S+/g, '[url]').slice(0, 240) : null,
      visibilityState: typeof document === 'undefined' ? 'unknown' : document.visibilityState,
      online: typeof navigator === 'undefined' ? null : navigator.onLine,
    };
    try { options.onTelemetry?.(report); } catch { /* Best effort. */ }
  };
  const onAbort = () => emit('browser_termination', 'abort_signal');
  const onPageHide = () => emit('browser_termination', 'pagehide');
  options.signal?.addEventListener('abort', onAbort, { once: true });
  if (typeof window !== 'undefined') window.addEventListener('pagehide', onPageHide);
  return {
    response(response: { headers?: { get(name: string): string | null } }, resumed: boolean) {
      clientRequestId ??= response.headers?.get('x-sudowrite-client-request-id') ?? null;
      const receivedInvocation = response.headers?.get('x-sudowrite-invocation-id');
      invocationId ??= receivedInvocation ?? null;
      httpRequestId = response.headers?.get('x-sudowrite-http-request-id') ?? null;
      if (!resumed) initialHttpRequestId = httpRequestId;
      if (resumed) connectionIndex++;
    },
    bytes(size: number) { if (size > 0) { firstByte ??= Date.now(); lastByte = Date.now(); bytes += size; } },
    frame(id: string | null) { lastFrame = Date.now(); frames++; if (id) lastReceivedEventId = id.slice(0, 128); },
    reconnect() { reconnectAttempts++; },
    attempt(status: number | null, error?: unknown) {
      if (attempts.length < 12) attempts.push({ status, errorName: errorName(error) });
    },
    connection(outcome: BrowserStreamReport['outcome'], error?: unknown) { if (emittedConnections++ < 20) emit('connection_ended', outcome, error); },
    finish(outcome: BrowserStreamReport['outcome'], error?: unknown) {
      // UI completion callbacks can throw after the transport has already finished.
      if (finished) return;
      finished = true;
      emit('session_ended', outcome, error);
      options.signal?.removeEventListener('abort', onAbort);
      if (typeof window !== 'undefined') window.removeEventListener('pagehide', onPageHide);
    },
  };
}
